import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ApiError } from "@/api/client";
import { api } from "@/api/endpoints";
import type { ConversationInfo, MessageInfo } from "@/api/types";
import { Composer } from "@/components/chat/Composer";
import { ConfirmModal } from "@/components/chat/ConfirmModal";
import { ConversationHeader } from "@/components/chat/ConversationHeader";
import { EmptyChat } from "@/components/chat/EmptyChat";
import { AssistantMessage, UserMessage } from "@/components/chat/Messages";
import { QueryLoading } from "@/components/chat/QueryLoading";
import { ChatSidebar } from "@/components/chat/Sidebar";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { useSession } from "@/store/session";

type LocalMessage = MessageInfo & { latencyMs?: number };

export function Chat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId: routeId } = useParams<{ conversationId?: string }>();
  const email = useSession((s) => s.email);
  const clearSession = useSession((s) => s.clear);

  const [conversations, setConversations] = useState<ConversationInfo[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [loadingConv, setLoadingConv] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const activeId = routeId ?? null;
  const active = useMemo(
    () => conversations.find((conversation) => conversation.id === activeId) ?? null,
    [conversations, activeId],
  );

  const refreshConversations = useCallback(async () => {
    try {
      const list = await api.conversations.list();
      setConversations(list);
      setListError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setListError(e instanceof Error ? e.message : "Could not load conversations.");
    } finally {
      setLoadingList(false);
    }
  }, [navigate]);

  useEffect(() => {
    void refreshConversations();
  }, [refreshConversations]);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    setLoadingConv(true);
    api.conversations
      .get(activeId)
      .then((res) => {
        if (cancelled) return;
        setMessages(res.messages ?? []);
        setConversations((prev) =>
          prev.some((conversation) => conversation.id === res.conversation.id)
            ? prev
            : [res.conversation, ...prev],
        );
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          navigate("/chat", { replace: true });
        } else if (e instanceof ApiError && e.status === 401) {
          navigate("/login", { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingConv(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeId, navigate]);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [activeId]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, thinking]);

  const send = useCallback(
    async (question: string) => {
      if (thinking) return;

      const optimisticUser: LocalMessage = {
        id: `pending_${Date.now()}`,
        role: "user",
        content: question,
        sources: [],
        created_at: Math.floor(Date.now() / 1000),
      };
      setMessages((prev) => [...prev, optimisticUser]);
      setThinking(true);

      try {
        const res = await api.query({
          question,
          conversation_id: activeId ?? undefined,
        });

        const assistant: LocalMessage = {
          id: `server_${Date.now()}`,
          role: "assistant",
          content: res.answer,
          sources: res.sources ?? [],
          created_at: Math.floor(Date.now() / 1000),
          latencyMs: res.latency_ms,
        };
        setMessages((prev) => [...prev, assistant]);

        if (!activeId && res.conversation_id) {
          navigate(`/chat/${res.conversation_id}`, { replace: true });
        }
        void refreshConversations();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        const msg =
          e instanceof ApiError && e.status === 429
            ? "You're sending questions too quickly. Please wait a moment."
            : e instanceof Error
              ? e.message
              : "Something went wrong. Please try again.";
        setMessages((prev) => [
          ...prev,
          {
            id: `error_${Date.now()}`,
            role: "assistant",
            content: `[!] ${msg}`,
            sources: [],
            created_at: Math.floor(Date.now() / 1000),
          },
        ]);
      } finally {
        setThinking(false);
      }
    },
    [activeId, navigate, refreshConversations, thinking],
  );

  const pendingState = location.state as { pendingQuestion?: string } | null;
  const handledPendingRef = useRef(false);
  useEffect(() => {
    if (handledPendingRef.current) return;
    if (!pendingState?.pendingQuestion) return;
    if (activeId) return;
    handledPendingRef.current = true;
    const question = pendingState.pendingQuestion;
    navigate(location.pathname, { replace: true, state: {} });
    void send(question);
  }, [pendingState, activeId, navigate, location.pathname, send]);

  const doDelete = useCallback(
    async (id: string) => {
      try {
        await api.conversations.delete(id);
        setConversations((prev) => prev.filter((conversation) => conversation.id !== id));
        if (id === activeId) {
          navigate("/chat", { replace: true });
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          navigate("/login", { replace: true });
        }
      } finally {
        setConfirmDeleteId(null);
      }
    },
    [activeId, navigate],
  );

  const onLogout = () => {
    const refreshToken = useSession.getState().refreshToken;
    if (refreshToken) void api.auth.logout({ refresh_token: refreshToken }).catch(() => {});
    clearSession();
    navigate("/", { replace: true });
  };

  return (
    <div className="sa-page sa-chat-page">
      <ChatSidebar
        conversations={conversations}
        activeId={activeId}
        onSelect={(id) => navigate(`/chat/${id}`)}
        onNew={() => navigate("/chat")}
        onDelete={(id) => setConfirmDeleteId(id)}
        onLogout={onLogout}
        email={email}
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
      />

      <main className="sa-chat-main">
        {loadingList && conversations.length === 0 && !activeId && !thinking ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--sa-muted-fg)",
              fontFamily: "var(--sa-sans)",
              fontSize: 13,
            }}
          >
            Loading...
          </div>
        ) : activeId ? (
          <>
            <ConversationHeader
              title={active?.title ?? (loadingConv ? "Loading..." : "Conversation")}
              onDelete={() => setConfirmDeleteId(activeId)}
              onMenu={() => setMobileSidebarOpen(true)}
            />
            <div
              ref={scrollRef}
              className="sa-chat-scroll"
              style={{ flex: 1, overflowY: "auto", background: "var(--sa-bg)" }}
            >
              <div className="sa-chat-thread">
                {messages.map((message) =>
                  message.role === "user" ? (
                    <UserMessage
                      key={message.id}
                      content={message.content}
                      time={formatTime(message.created_at)}
                    />
                  ) : (
                    <AssistantMessage
                      key={message.id}
                      answer={message.content}
                      sources={message.sources}
                      latencyMs={message.latencyMs}
                      time={formatTime(message.created_at)}
                    />
                  ),
                )}
                {thinking && <QueryLoading />}
                <div style={{ height: 24 }} />
              </div>
            </div>
            <Composer onSend={send} disabled={thinking} autoFocus />
          </>
        ) : (
          <>
            <header className="sa-chat-mobile-topbar">
              <button
                type="button"
                className="sa-chat-mobile-topbar__menu"
                onClick={() => setMobileSidebarOpen(true)}
                aria-label="Open conversations"
              >
                Menu
              </button>
              <div className="sa-chat-mobile-topbar__title">Submitter AI</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ThemeToggle />
                <button
                  type="button"
                  className="sa-chat-mobile-topbar__new"
                  onClick={() => navigate("/chat")}
                >
                  New
                </button>
              </div>
            </header>
            {thinking ? <QueryLoading variant="screen" /> : <EmptyChat onAsk={send} />}
          </>
        )}

        {listError && !activeId && (
          <div
            role="alert"
            style={{
              padding: "10px 18px",
              color: "var(--sa-danger)",
              fontFamily: "var(--sa-sans)",
              fontSize: 12,
              borderTop: "1px solid var(--sa-border)",
            }}
          >
            {listError}
          </div>
        )}
      </main>

      {confirmDeleteId && (
        <ConfirmModal
          title="Delete this conversation?"
          body="This inquiry and its responses will be permanently removed. This action cannot be undone."
          confirmLabel="Delete conversation"
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={() => doDelete(confirmDeleteId)}
        />
      )}
    </div>
  );
}

function formatTime(unixSec?: number): string | undefined {
  if (!unixSec) return undefined;
  const d = new Date(unixSec * 1000);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
